import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { FaFileAlt, FaTimes, FaTrash } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";

const AdminOrderHistoryModal = ({ isOpen, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    const q = query(collection(db, "orderHistory"), orderBy("date", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamp to milliseconds if needed
          date: doc.data().date?.toDate
            ? doc.data().date.toDate().getTime()
            : typeof doc.data().date === "string"
            ? parseCustomDate(doc.data().date)
            : doc.data().date,
        }));
        setOrders(ordersData.filter((order) => order.date));
        setLoading(false);
      },
      (error) => {
        toast.error("Error loading orders");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isOpen]);

  // Custom date parser that handles DD/MM/YYYY format
  const parseCustomDate = (dateString) => {
    if (!dateString) return null;

    // Try ISO format first
    if (!isNaN(Date.parse(dateString))) {
      return new Date(dateString).getTime();
    }

    // Try DD/MM/YYYY format
    const parts = dateString.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Months are 0-based
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }
    }

    console.error("Invalid date format:", dateString);
    return null;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Invalid date";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Invalid time";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid time";

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupOrdersByDate = () => {
    const grouped = {};
    orders.forEach((order) => {
      if (!order.date) return;

      const dateKey = formatDate(order.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          orders: [],
          timestamp: order.date,
        };
      }
      grouped[dateKey].orders.push(order);
    });
    return grouped;
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await deleteDoc(doc(db, "orderHistory", orderId));
        toast.success("Order deleted successfully");
      } catch (error) {
        toast.error("Failed to delete order");
        console.error("Error deleting order:", error);
      }
    }
  };

  const handleDeleteAllOrdersForDate = async (dateKey) => {
    const dateOrders = groupedOrders[dateKey];
    if (!dateOrders || dateOrders.orders.length === 0) return;

    if (window.confirm(`Delete ALL ${dateOrders.orders.length} orders for ${dateKey}?`)) {
      try {
        const deletePromises = dateOrders.orders.map((order) =>
          deleteDoc(doc(db, "orderHistory", order.id))
        );

        await Promise.all(deletePromises);
        toast.success(`Deleted ${dateOrders.orders.length} orders`);
      } catch (error) {
        toast.error("Failed to delete orders");
        console.error("Error deleting orders:", error);
      }
    }
  };

  const groupedOrders = groupOrdersByDate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center border-b p-3 sticky top-0 bg-white z-10">
          <h1 className="text-lg font-bold">Order History</h1>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={18} />
          </button>
        </div>

        <div className="p-3">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <ClipLoader color="#4B5563" size={25} />
            </div>
          ) : orders.length === 0 ? (
            <p className="text-center py-6 text-gray-500">No orders found</p>
          ) : (
            Object.keys(groupedOrders)
              .sort((a, b) => groupedOrders[b].timestamp - groupedOrders[a].timestamp)
              .map((dateKey) => (
                <div key={dateKey} className="mb-6 border rounded-lg overflow-hidden">
                  <div className="flex justify-between items-center bg-gray-100 p-3">
                    <h2 className="font-semibold text-lg">{dateKey}</h2>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-4">
                        {groupedOrders[dateKey].orders.length} orders
                      </span>
                      <button
                        onClick={() => handleDeleteAllOrdersForDate(dateKey)}
                        className="text-red-500 hover:text-red-700 text-sm flex items-center"
                      >
                        <FaTrash className="mr-1" size={14} />
                        Delete All
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Order ID
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Time
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupedOrders[dateKey].orders.map((order) => (
                          <tr key={order.id}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <FaFileAlt className="text-blue-500 mr-2" size={14} />
                                <span className="text-sm font-medium">{order.orderId}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              ₹{order.totalAmount}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {order.date}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleDeleteOrder(order.id)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete order"
                              >
                                <FaTrash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrderHistoryModal;
